import { useMemo, type ReactNode } from "react";
import { motion } from "motion/react";
import { useLiveFeed, type SpreadData } from "@/hooks/use-live-feed";
import { LogoLoop, type LogoItem } from "@/components/logo-loop";
import { Bone } from "@/components/ui/bone";

const ease = [0.16, 1, 0.3, 1] as const;

const DISPLAY_SYMBOLS = [
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "USDCHF",
  "AUDUSD",
  "USDCAD",
  "NZDUSD",
  "XAUUSD",
  "XAGUSD",
  "BTCUSD",
  "ETHUSD",
  "WTIUSD",
  "USTEC",
  "US500",
] as const;

const SYMBOL_META: Record<string, { label: string }> = {
  EURUSD: { label: "EUR/USD" },
  GBPUSD: { label: "GBP/USD" },
  USDJPY: { label: "USD/JPY" },
  USDCHF: { label: "USD/CHF" },
  AUDUSD: { label: "AUD/USD" },
  USDCAD: { label: "USD/CAD" },
  NZDUSD: { label: "NZD/USD" },
  XAUUSD: { label: "XAU/USD" },
  XAGUSD: { label: "XAG/USD" },
  BTCUSD: { label: "BTC/USD" },
  ETHUSD: { label: "ETH/USD" },
  WTIUSD: { label: "WTI Oil" },
  USTEC: { label: "NAS100" },
  US500: { label: "S&P 500" },
};

/** Alias map — broker symbols → canonical */
const ALIAS_MAP: Record<string, string> = {
  NDXUSD: "USTEC",
  US100: "USTEC",
  NASDAQ100: "USTEC",
  NQ: "USTEC",
  SPXUSD: "US500",
  SP500: "US500",
  SPX: "US500",
};

function normalize(symbol: string): string {
  const upper = symbol.toUpperCase().replace(/[.!#+_]/g, "").replace(/[A-Z]*$/, (m) => m);
  return ALIAS_MAP[upper] ?? upper;
}

function formatPrice(bid: number, digits: number): string {
  return bid.toFixed(digits);
}

function PriceCard({ item }: { item: SpreadData }) {
  const canonical = normalize(item.symbol);
  const meta = SYMBOL_META[canonical];
  if (!meta) return null;

  if (!item.isOpen) {
    return (
      <span className="inline-flex items-center gap-4 px-5 py-3 sm:px-6 sm:py-4 rounded-xl bg-muted/50 border border-border/50 select-none whitespace-nowrap">
        <span className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs sm:text-sm font-medium text-muted-foreground tracking-wide uppercase">
            {meta.label}
          </span>
          <span className="flex items-center gap-2">
            <span className="text-base sm:text-lg font-mono text-muted-foreground/50 tabular-nums">
              Closed
            </span>
            {item.change24h && (
              <span
                className={`text-xs sm:text-sm font-mono tabular-nums ${
                  item.change24h.startsWith("+")
                    ? "text-emerald-500/70"
                    : item.change24h.startsWith("-")
                      ? "text-red-500/70"
                      : "text-muted-foreground"
                }`}
              >
                {item.change24h}
              </span>
            )}
          </span>
        </span>
      </span>
    );
  }

  const price = formatPrice(item.bid, item.digits);
  const isUp = item.direction === "up";
  const isDown = item.direction === "down";

  return (
    <span className="inline-flex items-center gap-4 px-5 py-3 sm:px-6 sm:py-4 rounded-xl bg-muted/50 border border-border/50 select-none whitespace-nowrap">
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs sm:text-sm font-medium text-muted-foreground tracking-wide uppercase">
          {meta.label}
        </span>
        <span className="flex items-center gap-2">
          <span
            className={`text-base sm:text-lg font-mono font-semibold tabular-nums transition-colors duration-300 ${
              isUp
                ? "text-emerald-500"
                : isDown
                  ? "text-red-500"
                  : "text-foreground"
            }`}
          >
            {price}
          </span>
          {item.change24h && (
            <span
              className={`text-xs sm:text-sm font-mono tabular-nums ${
                item.change24h.startsWith("+")
                  ? "text-emerald-500/70"
                  : item.change24h.startsWith("-")
                    ? "text-red-500/70"
                    : "text-muted-foreground"
              }`}
            >
              {item.change24h}
            </span>
          )}
        </span>
      </span>
    </span>
  );
}

// Fixture used by boneyard's `npx boneyard-js build` to snapshot the card shape.
const FIXTURE_ITEM: SpreadData = {
  symbol: "EURUSD",
  bid: 1.08543,
  ask: 1.08551,
  spread_points: 0.8,
  digits: 5,
  direction: "up",
  isOpen: true,
  change24h: "+0.12%",
};

function SkeletonFallback(): ReactNode {
  // Boneyard uses this while the registry is empty (pre-`boneyard-js build`).
  // After running the CLI, boneyard swaps in pixel-perfect bones automatically.
  return (
    <span className="inline-flex items-center gap-4 px-5 py-3 sm:px-6 sm:py-4 rounded-xl bg-muted/50 border border-border/50 select-none whitespace-nowrap">
      <span className="flex flex-col gap-1.5 min-w-0">
        <span className="h-3.5 sm:h-4 w-14 rounded bg-muted-foreground/20 animate-pulse" />
        <span className="h-5 sm:h-6 w-24 rounded bg-muted-foreground/15 animate-pulse" />
      </span>
    </span>
  );
}

function SkeletonCard(): ReactNode {
  return (
    <Bone
      name="live-price-card"
      loading
      fixture={<PriceCard item={FIXTURE_ITEM} />}
      fallback={<SkeletonFallback />}
    >
      <span />
    </Bone>
  );
}

/**
 * True while `npx boneyard-js build` is crawling the app so we can force
 * skeleton rendering even after data has loaded — otherwise the bones CLI
 * never sees the Bone and captures nothing.
 */
const isBoneyardBuild =
  typeof window !== "undefined" &&
  (window as unknown as { __BONEYARD_BUILD?: boolean }).__BONEYARD_BUILD === true;

export function LivePricing(): ReactNode {
  const { data } = useLiveFeed("opofinance", "ecn");

  const priceMap = useMemo(() => {
    const map = new Map<string, SpreadData>();
    for (const item of data) {
      const key = normalize(item.symbol);
      map.set(key, item);
    }
    return map;
  }, [data]);

  const hasData = priceMap.size > 0 && !isBoneyardBuild;

  const logoItems: LogoItem[] = useMemo(() => {
    if (!hasData) {
      return DISPLAY_SYMBOLS.map((sym) => ({
        node: <SkeletonCard key={sym} />,
      }));
    }
    return DISPLAY_SYMBOLS.filter((sym) => priceMap.has(sym)).map((sym) => ({
      node: <PriceCard key={sym} item={priceMap.get(sym)!} />,
    }));
  }, [hasData, priceMap]);

  return (
    <section className="relative w-full">
      <div className="flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease }}
          className="w-full"
        >
          <LogoLoop
            logos={logoItems}
            speed={35}
            logoHeight={68}
            gap={16}
            pauseOnHover={true}
          />
        </motion.div>
      </div>
    </section>
  );
}
